<template>
	<GlassCard class="p-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<h2 class="mb-0">Podcasts</h2>
			<button
				class="btn btn-light"
				@click="fetchPodcasts(1)"
				:disabled="loading || generating"
				title="Refresh">
				<i class="bi bi-arrow-clockwise"></i>
			</button>
		</div>
		<div class="mb-4">
			<button
				v-if="canGenerate"
				class="btn btn-orange"
				@click="generatePodcast"
				:disabled="generating || loading">
				<span v-if="generating"
					><span class="spinner-border spinner-border-sm me-2"></span
					>Generating...</span
				>
				<span v-else>Generate Podcast</span>
			</button>
			<div
				v-else
				class="alert alert-info d-flex align-items-center"
				role="alert">
				<i class="bi bi-info-circle-fill me-3" style="font-size: 1.5rem"></i>
				<div>
					<h5 class="alert-heading mb-1">Next Podcast Available Soon!</h5>
					You can generate your next weekly podcast summary
					<strong>{{ nextGenerationTime }}</strong
					>, on {{ nextGenerationDateString }}.
				</div>
			</div>
		</div>
		<table class="table table-bordered table-hover align-middle">
			<thead class="table-light">
				<tr>
					<th>Start Date</th>
					<th>End Date</th>
					<th>Status</th>
				</tr>
			</thead>
			<tbody>
				<tr v-if="loading">
					<td colspan="3" class="text-center text-muted">Loading...</td>
				</tr>
				<tr v-for="podcast in podcasts" :key="podcast.id" v-else>
					<td>{{ podcast.start_date }}</td>
					<td>{{ podcast.end_date }}</td>
					<td>
						<button
							v-if="podcast.status === 'done'"
							class="btn btn-link p-0"
							title="Download"
							@click="downloadPodcast(podcast)">
							<i class="bi bi-arrow-down-circle" style="font-size: 1.5rem"></i>
						</button>
						<span
							v-else-if="podcast.status === 'pending'"
							class="badge bg-warning text-dark"
							>Pending</span
						>
						<span
							v-else-if="podcast.status === 'generating'"
							class="badge bg-info text-dark"
							>Generating</span
						>
						<span
							v-else-if="podcast.status === 'error'"
							class="badge bg-danger text-light"
							>Error</span
						>
					</td>
				</tr>
				<tr v-if="!loading && podcasts.length === 0">
					<td colspan="3" class="text-center text-muted">No podcasts yet.</td>
				</tr>
			</tbody>
		</table>
		<div class="d-flex justify-content-center mt-3">
			<nav aria-label="Podcast pagination">
				<ul class="pagination mb-0 flex-wrap">
					<li class="page-item" :class="{ disabled: currentPage === 1 }">
						<button
							class="page-link"
							@click="goToFirst"
							:disabled="currentPage === 1">
							First
						</button>
					</li>
					<li class="page-item" :class="{ disabled: currentPage === 1 }">
						<button
							class="page-link"
							@click="goToPrev"
							:disabled="currentPage === 1">
							Prev
						</button>
					</li>
					<li
						v-for="page in pageNumbers()"
						:key="page + '-pg'"
						class="page-item"
						:class="{ active: currentPage === page, disabled: page === '...' }">
						<span v-if="page === '...'" class="page-link">&hellip;</span>
						<button v-else class="page-link" @click="goToPage(page)">
							{{ page }}
						</button>
					</li>
					<li
						class="page-item"
						:class="{ disabled: currentPage >= totalPages }">
						<button
							class="page-link"
							@click="goToNext"
							:disabled="currentPage >= totalPages">
							Next
						</button>
					</li>
					<li
						class="page-item"
						:class="{ disabled: currentPage >= totalPages }">
						<button
							class="page-link"
							@click="goToLast"
							:disabled="currentPage >= totalPages">
							Last
						</button>
					</li>
				</ul>
			</nav>
		</div>
	</GlassCard>
</template>

<script setup>
	import { ref, computed, watch, onMounted } from "vue"
	import GlassCard from "./GlassCard.vue"
	import moment from "moment-timezone"
	import { fetchWithAuth } from "@/utils/api-client"
	import Swal from "sweetalert2"
	import getUrl from "@/utils/url"
	import * as SubscriptionHelper from "@/utils/subscriptionHelper"

	const podcasts = ref([])
	const total = ref(0)
	const pageSize = 10
	const currentPage = ref(1)
	const totalPages = computed(() => Math.ceil(total.value / pageSize))
	const loading = ref(false)
	const generating = ref(false)
	const lastPodcastEndDate = ref(null)
	const canGenerate = ref(false)
	const nextGenerationTime = ref("")
	const nextGenerationDateString = ref("")

	function updateGenerationStatus() {
		if (!lastPodcastEndDate.value) {
			canGenerate.value = true
			return
		}

		const today = moment().startOf("day")
		const lastGenerated = moment(lastPodcastEndDate.value).startOf("day")
		const nextAvailableDate = lastGenerated.clone().add(7, "days")

		if (today.isSameOrAfter(nextAvailableDate)) {
			canGenerate.value = true
		} else {
			canGenerate.value = false
			nextGenerationTime.value = nextAvailableDate.fromNow()
			nextGenerationDateString.value = nextAvailableDate.format("LL")
		}
	}

	async function downloadPodcast(podcast) {
		if (!podcast.file_url) {
			Swal.fire({
				icon: "error",
				title: "No file available",
				text: "This podcast does not have a downloadable file yet.",
			})
			return
		}
		const url = getUrl("fetch") + "/wav/" + podcast.file_url
		window.open(url, "_blank")
	}

	async function generatePodcast() {
		const feature = "Podcasts"
		if (SubscriptionHelper.isFreeAccount()) {
			SubscriptionHelper.showLockedModal(feature)
			return
		}

		generating.value = true
		try {
			const response = await fetchWithAuth("/api/podcast/generate", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
			})

			const data = await response.json()
			if (data.error) {
				throw new Error(data.error)
			}

			if (!response.ok) throw new Error("Failed to generate podcast")

			if (
				!(await SubscriptionHelper.requestCheckSubscription(response, feature))
			) {
				generating.value = false
				return
			}

			Swal.fire({
				icon: "success",
				title: "Podcast Generating",
				text: "You will receive an email when your podcast is ready for download (if system notifications are enabled).",
			})

			await fetchPodcasts(1)
		} catch (e) {
			Swal.fire({
				icon: "error",
				title: "Error",
				text: e.message,
			})
		} finally {
			generating.value = false
		}
	}

	async function fetchPodcasts(page = 1) {
		loading.value = true
		try {
			// Assuming an endpoint, this might need to be changed
			const response = await fetchWithAuth(
				`/api/podcast?page=${page}&pageSize=${pageSize}`
			)
			if (!response.ok) throw new Error("Failed to fetch podcasts")
			const data = await response.json()
			podcasts.value = data.podcasts
			total.value = data.total
			currentPage.value = data.page
			lastPodcastEndDate.value = data.lastPodcastEndDate
			updateGenerationStatus()
		} catch (e) {
			podcasts.value = []
			total.value = 0
			Swal.fire({
				icon: "error",
				title: "Error fetching podcasts",
				text: e.message,
			})
		} finally {
			loading.value = false
		}
	}

	onMounted(() => {
		fetchPodcasts(1)
		updateGenerationStatus()
	})

	watch(lastPodcastEndDate, () => {
		updateGenerationStatus()
	})

	watch(currentPage, (page) => {
		fetchPodcasts(page)
	})

	// const paginatedPodcasts = computed(() =>
	// podcasts.value.map((p) => ({
	// 	...p,
	// 	start_date: p.start_date,
	// 	end_date: p.end_date,
	// }))
	// )

	function goToPage(page) {
		if (page < 1 || page > totalPages.value) return
		currentPage.value = page
	}

	function goToFirst() {
		currentPage.value = 1
	}
	function goToLast() {
		currentPage.value = totalPages.value
	}
	function goToPrev() {
		if (currentPage.value > 1) currentPage.value--
	}
	function goToNext() {
		if (currentPage.value < totalPages.value) currentPage.value++
	}

	function pageNumbers() {
		const total = totalPages.value
		const current = currentPage.value
		if (total <= 7) {
			return Array.from({ length: total }, (_, i) => i + 1)
		}
		const pages = []
		pages.push(1, 2)
		let start = Math.max(3, current - 1)
		let end = Math.min(total - 2, current + 1)
		if (start > 3) pages.push("...")
		for (let i = start; i <= end; i++) {
			pages.push(i)
		}
		if (end < total - 2) pages.push("...")
		pages.push(total - 1, total)
		return pages.filter((v, i, a) => a.indexOf(v) === i)
	}
</script>

<style scoped>
	.btn-orange {
		background-color: #ff6400;
		color: white;
	}
	.btn-orange:hover {
		background-color: #ff6400;
		color: white;
		opacity: 0.9;
	}
</style>
